import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Mail, Plus, Settings, Trash2, Check, X, AlertCircle, 
  RefreshCw, Shield, ExternalLink, Inbox, Send, Clock,
  Loader2, CheckCircle, XCircle, ArrowRight, Bot, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const EmailChannelPage = () => {
  const [loading, setLoading] = useState(true);
  const [chatbots, setChatbots] = useState([]);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [connectionType, setConnectionType] = useState('gmail');
  const [selectedChatbot, setSelectedChatbot] = useState('');
  const [imapConfig, setImapConfig] = useState({
    email: '',
    password: '',
    imap_host: '',
    imap_port: '993',
    smtp_host: '',
    smtp_port: '587',
    use_ssl: true
  });
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [chatbotsRes, accountsRes] = await Promise.all([
        api.get('/chatbots'),
        api.get('/email-channels')
      ]);
      
      const chatbotsData = Array.isArray(chatbotsRes.data) ? chatbotsRes.data : chatbotsRes.data.chatbots || [];
      setChatbots(chatbotsData);
      setEmailAccounts(accountsRes.data || []);
      
      if (chatbotsData.length > 0 && !selectedChatbot) {
        setSelectedChatbot(chatbotsData[0].chatbot_id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Set empty arrays on error
      setEmailAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGmailConnect = async () => {
    if (!selectedChatbot) {
      toast.error('Please select an AI Employee first');
      return;
    }
    
    setConnecting(true);
    try {
      const response = await api.post('/email-channels/gmail/authorize', {
        chatbot_id: selectedChatbot
      });
      
      if (response.data.auth_url) {
        window.open(response.data.auth_url, '_blank', 'width=600,height=700');
        toast.info('Complete Gmail authorization in the popup window');
      }
    } catch (error) {
      toast.error('Failed to initiate Gmail connection');
    } finally {
      setConnecting(false);
    }
  };

  const handleImapConnect = async () => {
    if (!selectedChatbot) {
      toast.error('Please select an AI Employee first');
      return;
    }
    
    if (!imapConfig.email || !imapConfig.password) {
      toast.error('Email and password are required');
      return;
    }
    
    setConnecting(true);
    try {
      await api.post('/email-channels/imap', {
        chatbot_id: selectedChatbot,
        ...imapConfig
      });
      
      toast.success('Email account connected successfully!');
      setShowAddModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect email account');
    } finally {
      setConnecting(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!confirm('Are you sure you want to disconnect this email account?')) return;
    
    try {
      await api.delete(`/email-channels/${accountId}`);
      toast.success('Email account disconnected');
      loadData();
    } catch (error) {
      toast.error('Failed to disconnect email account');
    }
  };

  const handleToggleActive = async (accountId, active) => {
    try {
      await api.patch(`/email-channels/${accountId}`, { active });
      toast.success(active ? 'Email channel activated' : 'Email channel paused');
      loadData();
    } catch (error) {
      toast.error('Failed to update email channel');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Connected</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Error</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700 border-slate-200">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-channel-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-7 h-7 text-blue-600" />
            Email Channel Integration
          </h1>
          <p className="text-slate-600 mt-1">
            Connect email accounts to capture leads and automate responses
          </p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-email-btn">
              <Plus className="w-4 h-4 mr-2" />
              Connect Email
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Connect Email Account
              </DialogTitle>
              <DialogDescription>
                Connect an email account to receive and respond to leads automatically.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Select AI Employee */}
              <div className="space-y-2">
                <Label className="text-slate-700">AI Employee</Label>
                <Select value={selectedChatbot} onValueChange={setSelectedChatbot}>
                  <SelectTrigger data-testid="chatbot-select">
                    <SelectValue placeholder="Select AI Employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {chatbots.map((bot) => (
                      <SelectItem key={bot.chatbot_id} value={bot.chatbot_id}>
                        <span className="flex items-center gap-2">
                          <Bot className="w-4 h-4" />
                          {bot.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">This AI Employee will respond to emails received</p>
              </div>
              
              {/* Connection Type Tabs */}
              <Tabs value={connectionType} onValueChange={setConnectionType}>
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="gmail" className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                    </svg>
                    Gmail
                  </TabsTrigger>
                  <TabsTrigger value="imap" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    IMAP/SMTP
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="gmail" className="space-y-4 mt-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Easy Gmail Connection</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Connect securely with Google OAuth. No password required.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={handleGmailConnect} 
                    disabled={connecting || !selectedChatbot}
                    className="w-full bg-white border-2 border-slate-200 text-slate-900 hover:bg-slate-50"
                    data-testid="gmail-connect-btn"
                  >
                    {connecting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    )}
                    {connecting ? 'Connecting...' : 'Connect with Google'}
                  </Button>
                </TabsContent>
                
                <TabsContent value="imap" className="space-y-4 mt-4">
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-900">IMAP/SMTP Configuration</p>
                        <p className="text-sm text-amber-700 mt-1">
                          For advanced users. Requires server settings from your email provider.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label className="text-slate-700">Email Address</Label>
                        <Input 
                          type="email"
                          placeholder="you@company.com"
                          value={imapConfig.email}
                          onChange={(e) => setImapConfig({ ...imapConfig, email: e.target.value })}
                          data-testid="imap-email"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-700">Password / App Password</Label>
                        <Input 
                          type="password"
                          placeholder="Enter password"
                          value={imapConfig.password}
                          onChange={(e) => setImapConfig({ ...imapConfig, password: e.target.value })}
                          data-testid="imap-password"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700">IMAP Host</Label>
                        <Input 
                          placeholder="imap.gmail.com"
                          value={imapConfig.imap_host}
                          onChange={(e) => setImapConfig({ ...imapConfig, imap_host: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">IMAP Port</Label>
                        <Input 
                          type="number"
                          placeholder="993"
                          value={imapConfig.imap_port}
                          onChange={(e) => setImapConfig({ ...imapConfig, imap_port: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700">SMTP Host</Label>
                        <Input 
                          placeholder="smtp.gmail.com"
                          value={imapConfig.smtp_host}
                          onChange={(e) => setImapConfig({ ...imapConfig, smtp_host: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">SMTP Port</Label>
                        <Input 
                          type="number"
                          placeholder="587"
                          value={imapConfig.smtp_port}
                          onChange={(e) => setImapConfig({ ...imapConfig, smtp_port: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-700">Use SSL/TLS</Label>
                      <Switch 
                        checked={imapConfig.use_ssl}
                        onCheckedChange={(checked) => setImapConfig({ ...imapConfig, use_ssl: checked })}
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleImapConnect}
                    disabled={connecting || !selectedChatbot}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="imap-connect-btn"
                  >
                    {connecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Connect Email Account
                      </>
                    )}
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* How It Works */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Inbox className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">1. Receive Emails</p>
                <p className="text-sm text-slate-600">Incoming emails are captured</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-blue-400 rotate-90 md:rotate-0" />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Bot className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">2. AI Processes</p>
                <p className="text-sm text-slate-600">AI extracts lead info & responds</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-blue-400 rotate-90 md:rotate-0" />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Zap className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">3. Lead Created</p>
                <p className="text-sm text-slate-600">Auto-added to your CRM pipeline</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connected Accounts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Shield className="w-5 h-5 text-slate-600" />
            Connected Email Accounts
          </CardTitle>
          <CardDescription>
            Manage your connected email accounts for lead capture
          </CardDescription>
        </CardHeader>
        <CardContent>
          {emailAccounts.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
              <Mail className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Email Accounts Connected</h3>
              <p className="text-slate-600 mb-4 max-w-sm mx-auto">
                Connect an email account to start capturing leads from incoming emails.
              </p>
              <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Connect Your First Email
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {emailAccounts.map((account) => (
                <div 
                  key={account.account_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`email-account-${account.account_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                      {account.provider === 'gmail' ? (
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                        </svg>
                      ) : (
                        <Mail className="w-5 h-5 text-slate-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{account.email}</p>
                      <p className="text-sm text-slate-500">
                        Connected to: {chatbots.find(b => b.chatbot_id === account.chatbot_id)?.name || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(account.status)}
                    <Switch 
                      checked={account.active}
                      onCheckedChange={(checked) => handleToggleActive(account.account_id, checked)}
                    />
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDeleteAccount(account.account_id)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            Tips for Email Channel
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
              <p>Create a dedicated email for lead capture (e.g., leads@company.com)</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
              <p>For Gmail, ensure "Less secure app access" is enabled or use App Passwords</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
              <p>Train your AI Employee with common email inquiry templates</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
              <p>Set up auto-responders in your AI Employee's rules for instant replies</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailChannelPage;
